import React from 'react';
import { FloorPlanPoint } from '../../../types';
import {
  formatMeters,
  canvasDistanceToMeters,
  DraftSnapResult,
} from '../../../utils/floorPlanGeometry';

interface WallDraftPreviewProps {
  start: FloorPlanPoint | null;
  current: FloorPlanPoint | null;
  snap?: DraftSnapResult | null;
  /** Existing wall endpoints available to snap to */
  magnets?: FloorPlanPoint[];
  pixelsPerMeter: number;
  units: 'metric' | 'imperial';
  lengthInput: string;
  onLengthInputChange: (value: string) => void;
  measureOnly?: boolean;
  /** Canvas zoom so the snap cursor stays a readable screen size */
  canvasZoom?: number;
}

const GRID_SNAP_COLOR = '#4ade80';

function GridSnapMarker({ x, y, zoom = 1 }: { x: number; y: number; zoom?: number }) {
  const scale = 1 / Math.max(0.2, zoom);
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <line x1={-12} y1={0} x2={12} y2={0} stroke={GRID_SNAP_COLOR} strokeWidth={1.5} />
      <line x1={0} y1={-12} x2={0} y2={12} stroke={GRID_SNAP_COLOR} strokeWidth={1.5} />
      <rect
        x={-4}
        y={-4}
        width={8}
        height={8}
        fill="rgba(15, 23, 42, 0.85)"
        stroke={GRID_SNAP_COLOR}
        strokeWidth={1.5}
      />
      <circle cx={0} cy={0} r={1.6} fill={GRID_SNAP_COLOR} />
    </g>
  );
}

const WallDraftPreview: React.FC<WallDraftPreviewProps> = ({
  start,
  current,
  snap,
  magnets = [],
  pixelsPerMeter,
  units,
  lengthInput,
  onLengthInputChange,
  measureOnly = false,
  canvasZoom = 1,
}) => {
  const snapped = !!snap?.kind;
  const drafting = !!(start && current);
  const gridSnapPoint =
    snap?.gridSnapped || snap?.kind === 'grid'
      ? drafting
        ? current
        : snap.point
      : null;
  const snapColor =
    snap?.kind === 'endpoint'
      ? '#f472b6'
      : snap?.kind === 'axis'
        ? '#22d3ee'
        : snap?.kind === 'length'
          ? '#a78bfa'
          : snap?.kind === 'grid'
            ? GRID_SNAP_COLOR
            : '#60a5fa';

  return (
    <svg
      className="absolute top-0 left-0 overflow-visible pointer-events-none"
      width={100000}
      height={100000}
      style={{ zIndex: 5 }}
    >
      {magnets.map((p, i) => {
        const isActive =
          snap?.kind === 'endpoint' &&
          snap.matchedPoint &&
          Math.abs(snap.matchedPoint.x - p.x) < 0.5 &&
          Math.abs(snap.matchedPoint.y - p.y) < 0.5;
        return (
          <g key={`magnet-${i}`}>
            <circle
              cx={p.x}
              cy={p.y}
              r={isActive ? 7 : 4}
              fill={isActive ? '#f472b6' : 'rgba(244, 114, 182, 0.35)'}
              stroke={isActive ? '#fff' : '#f9a8d4'}
              strokeWidth={isActive ? 1.5 : 1}
            />
            {isActive && (
              <circle
                cx={p.x}
                cy={p.y}
                r={12}
                fill="none"
                stroke="#f472b6"
                strokeWidth={1.25}
                opacity={0.8}
              />
            )}
          </g>
        );
      })}

      {drafting && (
        <>
          {/* CAD guide: axis alignment to opposite point */}
          {snap?.kind === 'axis' && snap.matchedPoint && (
            <>
              <line
                x1={snap.matchedPoint.x}
                y1={snap.matchedPoint.y}
                x2={current!.x}
                y2={current!.y}
                stroke="#22d3ee"
                strokeWidth={1}
                strokeDasharray="4 3"
                opacity={0.85}
              />
              <circle
                cx={snap.matchedPoint.x}
                cy={snap.matchedPoint.y}
                r={5}
                fill="none"
                stroke="#22d3ee"
                strokeWidth={1.5}
              />
            </>
          )}

          {snap?.kind === 'length' && (
            <text
              x={(start!.x + current!.x) / 2}
              y={(start!.y + current!.y) / 2 - 22}
              textAnchor="middle"
              fill="#a78bfa"
              fontSize={10}
              style={{ userSelect: 'none' }}
            >
              equal
            </text>
          )}

          <line
            x1={start!.x}
            y1={start!.y}
            x2={current!.x}
            y2={current!.y}
            stroke={measureOnly ? '#38bdf8' : snapColor}
            strokeWidth={measureOnly ? 1.5 : snapped ? 3 : 2.5}
            strokeDasharray={measureOnly ? '6 4' : undefined}
          />
          <circle cx={start!.x} cy={start!.y} r={4} fill="#60a5fa" />
          {!gridSnapPoint && (
            <circle
              cx={current!.x}
              cy={current!.y}
              r={snapped ? 5 : 4}
              fill={snapped ? snapColor : '#93c5fd'}
              stroke={snapped ? '#fff' : undefined}
              strokeWidth={snapped ? 1 : undefined}
            />
          )}

          {(() => {
            const px = Math.hypot(current!.x - start!.x, current!.y - start!.y);
            const meters = canvasDistanceToMeters(px, pixelsPerMeter);
            const label = formatMeters(meters, units);
            const midX = (start!.x + current!.x) / 2;
            const midY = (start!.y + current!.y) / 2;
            return (
              <foreignObject x={midX + 8} y={midY - 18} width={140} height={36}>
                <div className="flex items-center gap-1 pointer-events-auto">
                  {!measureOnly && (
                    <input
                      value={lengthInput}
                      onChange={(e) => onLengthInputChange(e.target.value)}
                      placeholder={units === 'imperial' ? 'ft' : 'm'}
                      className="w-16 px-1.5 py-0.5 text-[11px] rounded bg-slate-900/90 border border-blue-500 text-white focus:outline-none"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  )}
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-900/90 text-blue-200 border border-slate-600 whitespace-nowrap">
                    {lengthInput
                      ? `${lengthInput} ${units === 'imperial' ? 'ft' : 'm'}`
                      : label}
                  </span>
                </div>
              </foreignObject>
            );
          })()}
        </>
      )}

      {gridSnapPoint && (
        <GridSnapMarker x={gridSnapPoint.x} y={gridSnapPoint.y} zoom={canvasZoom} />
      )}
    </svg>
  );
};

export default WallDraftPreview;
